import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Go2rtcVideo } from './Go2rtcVideo.js';

export interface StreamMeta {
  streamName: string;
  cropX: number;
  cropY: number;
  cropW: number;
  cropH: number;
  /** Admin-set width as a percentage of the viewport (5–80). On the
   *  kiosk this drives the camera column's width. */
  displayPct: number;
}

export interface VisionDetection {
  id: string;
  /** Full-frame normalised hit position. */
  xNorm: number;
  yNorm: number;
  /** 1-based display order within the round (assigned by parent). */
  ord: number;
  valueNumeric: number | null;
  zoneCode: string | null;
}

/** Throws on a non-OK response (or network error); `null` = OK without data. */
async function fetchTrpc<T>(procedure: string, input: unknown): Promise<T | null> {
  const url = `/api/trpc/${procedure}?input=${encodeURIComponent(JSON.stringify(input))}&batch=0`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`${procedure}: HTTP ${res.status}`);
  const body = (await res.json()) as { result?: { data?: T } };
  return body.result?.data ?? null;
}

/**
 * Fetch the per-session stream metadata (relay name, crop, display
 * size) and re-poll every 30 s so admin changes propagate without a
 * board reload. Returns `null` until the first response, and stays
 * `null` if the location has no stream attached — the kiosk uses that
 * to hide the "Kamera" panel switcher button.
 */
export function useStreamMeta(sessionId: string | null): StreamMeta | null {
  const [meta, setMeta] = useState<StreamMeta | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let aborted = false;
    const refetch = async () => {
      // A failed poll (5xx, network) keeps the last meta — only an OK
      // answer without a stream hides the camera.
      try {
        const m = await fetchTrpc<StreamMeta>('public.sessionStream', { sessionId });
        if (!aborted) setMeta(m);
      } catch {
        // Keep the last meta; the next poll retries.
      }
    };
    void refetch();
    const t = window.setInterval(() => void refetch(), 30_000);
    return () => {
      aborted = true;
      window.clearInterval(t);
    };
  }, [sessionId]);

  return meta;
}

interface CameraPanelProps {
  meta: StreamMeta;
  className?: string;
  /**
   * `inline` (default) — peer panel of the leaderboard, rendered in
   *   place by the parent flex grid. Sized to `displayPct` viewport
   *   width with a height derived from the cropped aspect.
   * `overlay` — fixed picture-in-picture, anchored bottom-right and
   *   floating over the leaderboards. Same size, just out-of-flow.
   *   Used when the kiosk shows two leaderboards and still wants to
   *   show the camera.
   */
  mode?: 'inline' | 'overlay';
  /** Numbered CV-detected hit markers to draw on top of the live
   *  image. Caller filters / sorts; the panel just renders them
   *  through the same crop transform as the video. */
  detections?: VisionDetection[];
  /** Optional extra overlay node rendered above the video and the
   *  detection layer. Lets consumers add their own chrome (e.g. a
   *  close button on a referee-side floating tile) without forking
   *  the panel. */
  extraOverlay?: ReactNode;
  /** 0..1. Applied as `style.opacity` on the outer wrapper so the
   *  whole tile (border + video + overlay) fades together. Used by
   *  the kiosk to dial down the PiP without turning it off. Defaults
   *  to fully opaque. */
  opacity?: number;
}

/**
 * Live camera tile. The container is sized in `vw` units (admin's
 * `displayPct` for width, derived from cropped aspect for height) so
 * the rendered box is independent of whatever flex context the parent
 * imposes — explicit dimensions in both axes mean no layout pass can
 * accidentally squash or stretch the crop window.
 *
 * Crop is applied CSS-only: the inner <video> is sized as a multiple
 * of the *container* and offset by the same scale, so the cropped
 * region maps onto the full container regardless of its rendered
 * size. No canvas, no re-encoding. Hands transport off to
 * Go2rtcVideo, which negotiates MSE-LD first and falls back to
 * WebRTC.
 *
 * In `overlay` mode the same box is wrapped in `position: fixed`
 * anchored bottom-right with a z-index above the leaderboard, so it
 * coexists with two scoreboards as a true picture-in-picture.
 *
 * When detections are provided, an SVG overlay (DetectionOverlay)
 * draws numbered hit markers anchored to the same crop window as the
 * video — admin re-framing moves both in lockstep.
 */
export function CameraPanel({
  meta,
  className,
  mode = 'inline',
  detections = [],
  extraOverlay,
  opacity,
}: CameraPanelProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Source frame dimensions — needed only to compute the container's
  // aspect. Inner-video positioning is container-relative, so it
  // doesn't depend on `natural` at all.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const sync = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setNatural({ w: video.videoWidth, h: video.videoHeight });
      }
    };
    // `loadedmetadata` covers the initial decode; `resize` covers the
    // case where the source changes dimensions mid-stream (e.g. the
    // camera rotates or the relay swaps fallback transports).
    video.addEventListener('loadedmetadata', sync);
    video.addEventListener('resize', sync);
    sync();
    return () => {
      video.removeEventListener('loadedmetadata', sync);
      video.removeEventListener('resize', sync);
    };
  }, [meta.streamName]);

  // Container aspect = (cropW × natW) / (cropH × natH). Defaulting to
  // 16:9 keeps the layout sensible for the brief moment before
  // metadata lands.
  const naturalAspect = natural ? natural.w / Math.max(natural.h, 1) : 16 / 9;
  const cropAspect = (meta.cropW / Math.max(meta.cropH, 0.01)) * naturalAspect;
  // PiP and inline have different sizing semantics:
  //   - inline uses the admin-set `displayPct` (the "halber Bildschirm
  //     ↔ klein" knob in /admin/locations) because that knob's whole
  //     job is "how prominent should the camera column be";
  //   - PiP is a true picture-in-picture floating *over* the
  //     leaderboards, so it always renders at PIP_VW regardless of
  //     displayPct. Letting it inherit a 50–80 % displayPct would
  //     cover the very scoreboards it's supposed to coexist with.
  const containerWvw = mode === 'overlay' ? PIP_VW : meta.displayPct;
  const containerHvw = containerWvw / Math.max(cropAspect, 0.01);
  // Inner video sized as a multiple of the container, so the crop
  // region maps onto the full container regardless of the container's
  // actual rendered size. cropW = 0.5 → inner = 200% of container.
  const innerWidthPct = 100 / Math.max(meta.cropW, 0.01);
  const innerHeightPct = 100 / Math.max(meta.cropH, 0.01);
  // Offsets in % of container — same coordinate space as the inner
  // dimensions, so they compose cleanly.
  const offsetXPct = -meta.cropX * innerWidthPct;
  const offsetYPct = -meta.cropY * innerHeightPct;

  const cameraNode = (
    <Go2rtcVideo
      streamName={meta.streamName}
      videoRef={videoRef}
      hideStateOverlay
      // `relative` is also added by Go2rtcVideo, but spelled here too
      // so the absolute child's containing block is explicit.
      className={`relative overflow-hidden rounded-lg border-2 border-zinc-700 bg-black shadow-xl ${className ?? ''}`}
      style={{ width: `${containerWvw}vw`, height: `${containerHvw}vw` }}
      videoClassName="absolute"
      videoStyle={{
        width: `${innerWidthPct}%`,
        height: `${innerHeightPct}%`,
        left: `${offsetXPct}%`,
        top: `${offsetYPct}%`,
        maxWidth: 'none',
        // Force `fill` so the video occupies its (already aspect-correct)
        // box without an internal letterbox — defends against UA quirks
        // where <video> defaults to `contain`.
        objectFit: 'fill',
      }}
      overlay={
        <>
          {detections.length > 0 && (
            <DetectionOverlay meta={meta} detections={detections} />
          )}
          {extraOverlay}
        </>
      }
    />
  );

  // Outer-wrapper opacity — only set when the caller dials it down,
  // so callers that don't pass the prop get pixel-identical output to
  // before this prop existed.
  const opacityStyle = opacity != null && opacity < 1 ? { opacity } : undefined;
  if (mode === 'overlay') {
    return (
      // z-30 sits above the leaderboard text. Bottom/right margin
      // matches the inline panel's self-end anchoring.
      <div
        className="pointer-events-none fixed right-4 bottom-4 z-30"
        style={opacityStyle}
      >
        {cameraNode}
      </div>
    );
  }
  if (opacityStyle) {
    return <div style={opacityStyle}>{cameraNode}</div>;
  }
  return cameraNode;
}

/** Picture-in-picture default width as a fraction of the viewport
 *  (vw). Independent of the admin's `displayPct` because PiP is a
 *  fundamentally different layout: it floats over the scoreboards
 *  rather than being a column next to them. */
export const PIP_VW = 22;

/**
 * SVG layer pinned to the same crop window as the visible video. Each
 * detection is drawn as a numbered hole at its full-frame normalised
 * (xNorm, yNorm), translated through the crop transform.
 */
export function DetectionOverlay({
  meta,
  detections,
}: {
  meta: StreamMeta;
  detections: VisionDetection[];
}) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden
    >
      {detections.map((d) => {
        const px = (d.xNorm - meta.cropX) / Math.max(meta.cropW, 0.01);
        const py = (d.yNorm - meta.cropY) / Math.max(meta.cropH, 0.01);
        if (px < 0 || px > 1 || py < 0 || py > 1) return null;
        const cx = px * 100;
        const cy = py * 100;
        return (
          <g key={d.id}>
            <circle
              cx={cx}
              cy={cy}
              r={2.6}
              fill={fillForDetection(d)}
              stroke="#0a0a0a"
              strokeWidth={0.4}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={cx}
              y={cy + 0.15}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={2.6}
              fontWeight={800}
              fill="#0a0a0a"
            >
              {d.ord}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function fillForDetection(d: VisionDetection): string {
  if (d.zoneCode) {
    switch (d.zoneCode) {
      case 'A':
        return '#10b981';
      case 'C':
        return '#84cc16';
      case 'D':
        return '#eab308';
      default:
        return '#ef4444';
    }
  }
  const v = d.valueNumeric;
  if (v == null) return '#71717a';
  if (v >= 9) return '#10b981';
  if (v >= 7) return '#84cc16';
  if (v >= 5) return '#eab308';
  if (v >= 3) return '#f97316';
  return '#ef4444';
}
